import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomSheet, FloatingTabBar, NavItem } from '../design-system';
import type { FloatingTabBarItem, SidebarNavItem } from '../design-system';
import { AccountMenu } from './AccountMenu';
import { clearPendingTryChat } from '../../lib/tryChatHandoff';
import { KeyboardCollapse } from './KeyboardCollapse';
import { NotificationsPopover } from './NotificationsPopover';
import { FaqModal } from '../FaqModal';
import { SupportModal } from '../SupportModal';
import { FeedbackPopover } from '../FeedbackPopover';

/**
 * Mobile nav for a signed-in traveler outside a trip (/try, /settings): the
 * same FloatingTabBar + "More" sheet as TripLayout, minus the trip-specific
 * tabs. "Chat" goes through /home, which resolves to the traveler's current
 * conversation (a trip's host chat, or Alma at /try).
 */
export function TravelerTabBar({ active, fade = false }: { active: 'chat' | 'more'; /** A question sheet sits right above: fade the band behind the bar into the page. */ fade?: boolean }) {
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const moreItems: SidebarNavItem[] = [
    // Not on /try itself ('chat'): that's already where a trip is planned.
    // Always a fresh chat — an unfinished draft is discarded first.
    ...(active !== 'chat'
      ? [{ key: 'new-trip', icon: 'plus', label: 'Plan a new trip', onClick: () => { clearPendingTryChat(); navigate('/try'); } }]
      : []),
    { key: 'faq', icon: 'help-circle', label: 'FAQ', onClick: () => setShowFaq(true) },
    { key: 'support', icon: 'headset', label: 'Support', onClick: () => setShowSupport(true) },
    { key: 'notifications', icon: 'bell', label: 'Notifications', onClick: () => setShowNotifications((v) => !v) },
    { key: 'feedback', icon: 'message-2', label: 'Feedback', onClick: () => setShowFeedback((v) => !v) },
  ];
  const tabItems: FloatingTabBarItem[] = [
    { key: 'chat', icon: 'message-circle', label: 'Chat', onClick: () => { if (active !== 'chat') navigate('/home'); } },
    { key: 'more', icon: 'dots', label: 'More', onClick: () => setMoreOpen(true) },
  ];

  return (
    <>
      <KeyboardCollapse className="md:hidden">
        <div ref={anchorRef}>
          <FloatingTabBar items={tabItems} active={active} fade={fade} />
        </div>
      </KeyboardCollapse>

      {moreOpen && (
        <BottomSheet title="More" onClose={() => setMoreOpen(false)}>
          <div className="flex flex-col gap-0.5">
            {moreItems.map((item) => (
              <NavItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                onClick={() => { item.onClick?.(); setMoreOpen(false); }}
              />
            ))}
          </div>
          <div style={{ borderTop: '1px solid var(--line)', margin: '10px 8px' }} />
          <div className="px-2 pb-1">
            <AccountMenu variant="quiet" onNavigate={() => setMoreOpen(false)} />
          </div>
        </BottomSheet>
      )}

      {showFaq && <FaqModal audience="traveler" onClose={() => setShowFaq(false)} />}
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
      {showFeedback && <FeedbackPopover anchorRef={anchorRef} onClose={() => setShowFeedback(false)} />}
      {showNotifications && <NotificationsPopover anchorRef={anchorRef} onClose={() => setShowNotifications(false)} />}
    </>
  );
}

export default TravelerTabBar;
